/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Checkbox, CardContent, Grid, TextField } from "@material-ui/core";
import { getLanguages } from "../../../promptsManager/services/languageServices";
import { getNamespaces } from "../../../promptsManager/services/namespaceServices";
import { updateClosedDataAsync, getClosedDataAsync } from "../../services/ClosedMessagesService";
import { PromptsSelector } from "./PromptsSelector";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { useCallback } from "react";
import { EmptyPlaceholder } from "../../../../components";
import { ActionButtons } from "../../../../components/Common";

const COMPONENT_NAME = "Admin settingsManager/components/QueueManagement/ClosedMessagesTab";

const getNewSettingsObject = (queueId) => {
	return {
		queueId,
		closingSoonTime: 5,
		closingSoonRepeatTime: 1,
		closingSoonPrompt: undefined,
		closingSoonPromptDisabled: true,
		closedPromptDisabled: true,
		closedPrompt: undefined,
	};
};

const ClosedMessagesTab = ({ queue, showSnackbar, isLoading, setIsLoading, setTabModified }) => {
	const [settings, setSettings] = React.useState(undefined);
	const [loadedSettings, setLoadedSettings] = React.useState(undefined);
	const [formEdited, setFormEdited] = React.useState(false);
	const [lookups, setLookups] = React.useState({ languages: undefined, namespaces: undefined });

	const errors = {};
	const { closingSoonTime, closingSoonRepeatTime } = settings || {};
	errors.closingSoonTime = !closingSoonTime || String(closingSoonTime).length > 10;
	errors.closingSoonRepeatTime =
		!closingSoonRepeatTime || closingSoonRepeatTime > closingSoonTime || String(closingSoonRepeatTime).length > 10;

	const onContentModified = useCallback(
		(state) => {
			setFormEdited(state);
			setTabModified(state);
		},
		[setFormEdited, setTabModified],
	);

	React.useEffect(() => {
		const init = async () => {
			setIsLoading(true);
			try {
				const [languages, namespaces, newSettings] = await Promise.all([
					getLanguages(),
					getNamespaces(),
					getClosedDataAsync(queue?.Id),
				]);
				setLookups({ languages, namespaces });
				setLoadedSettings(newSettings || getNewSettingsObject(queue?.Id));
				setSettings(newSettings || getNewSettingsObject(queue?.Id));
				setFormEdited(false);
			} catch (error) {
				showSnackbar("Failed to load configurations.", "error");
			} finally {
				setIsLoading(false);
			}
		};
		if (queue) {
			init();
		}
	}, [queue, setIsLoading, showSnackbar]);

	const saveClosedMessages = async () => {
		setIsLoading(true);
		try {
			setIsLoading(true);
			let settingsObject = { ...settings };
			await updateClosedDataAsync(settingsObject, queue?.Id);
			setLoadedSettings(settings);
			onContentModified(false);
			setIsLoading(false);
			showSnackbar("Successfully saved closed messages.", "success");
		} catch (error) {
			v2.error(`Error while saving closed messages`, {
				additionalData: { error: errorToObject(error), data: { settings, queueId: queue?.Id } },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.SAVE_CLOSED_MESSAGES_ERRORS,
			});
			showSnackbar("Failed to save closed messages.", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const setNewSettings = (newSettingsList) => {
		let newSettings = { ...settings };
		newSettingsList.forEach((element) => {
			newSettings[element.fieldName] = element.newValue;
			setSettings(newSettings);
		});
		onContentModified(true);
	};

	const onCancelClick = () => {
		setSettings(loadedSettings);
		onContentModified(false);
	};

	let saveDisabled =
		settings !== undefined &&
		((!settings?.closingSoonPromptDisabled &&
			(errors.closingSoonTime || errors.closingSoonRepeatTime || settings?.closingSoonPrompt === undefined)) ||
			(!settings?.closedPromptDisabled && settings?.closedPrompt === undefined));

	const isLookupsLoaded = !!settings && lookups.languages?.length > 0 && lookups.namespaces?.length > 0;

	return (
		<CardContent>
			<>
				<Grid container alignItems="center" justifyContent="flex-end" wrap="wrap">
					<ActionButtons
						showCancel={formEdited}
						showSave={formEdited}
						className="d-flex justify-content-end && my-2 my-md-0"
						onCancel={onCancelClick}
						onSave={saveClosedMessages}
						disableSave={saveDisabled}
					/>
				</Grid>
				<div className="d-flex align-items-center justify-content-between">
					{!isLoading && !isLookupsLoaded ? (
						<EmptyPlaceholder
							message="There was a problem loading the languages and namespaces from the prompt manager. Please use the prompt
						manager to create languages and namespaces for you prompts."
						/>
					) : (
						<div className="d-flex flex-column w-100">
							<div className="col-12 px-0">
								<Grid container justifyContent="space-between" spacing={2}>
									<Grid item xs={12} className="mt-4 pb-0">
										<strong>
											<span className="w-100 m-0">
												Closing Soon Prompt
												<Checkbox
													onChange={(event) => {
														onContentModified(true);
														setNewSettings([
															{ newValue: !event.target.checked, fieldName: "closingSoonPromptDisabled" },
														]);
													}}
													checked={!settings?.closingSoonPromptDisabled}
												></Checkbox>
											</span>
										</strong>
									</Grid>
									<Grid item xs={12} md={6}>
										<TextField
											id="closing-soon-time"
											type="number"
											label="Closing Soon Time (min)"
											value={settings?.closingSoonTime}
											InputLabelProps={{
												shrink: true,
											}}
											className="w-100"
											disabled={settings?.closingSoonPromptDisabled}
											InputProps={{ inputProps: { min: 0 } }}
											onChange={(event) => {
												const newClosingSoonTime = Math.max(event.target.value, 0);

												setNewSettings([
													{ newValue: newClosingSoonTime, fieldName: "closingSoonTime" },
													{
														newValue: Math.min(newClosingSoonTime, settings?.closingSoonRepeatTime),
														fieldName: "closingSoonRepeatTime",
													},
												]);
											}}
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<TextField
											id="closing-soon-repeat-time"
											type="number"
											value={settings?.closingSoonRepeatTime}
											InputLabelProps={{
												shrink: true,
											}}
											label="Closing Soon Repeat Rate (min)"
											className="w-100"
											disabled={settings?.closingSoonPromptDisabled}
											InputProps={{ inputProps: { min: 0, max: settings?.closingSoonTime } }}
											onChange={(event) => {
												setNewSettings([
													{
														newValue: Math.min(Math.max(event.target.value, 0), settings?.closingSoonTime),
														fieldName: "closingSoonRepeatTime",
													},
												]);
											}}
										/>
									</Grid>
									{lookups.languages && lookups.namespaces && (
										<PromptsSelector
											languages={lookups.languages}
											namespaces={lookups.namespaces}
											prompt={settings?.closingSoonPrompt}
											disabled={settings?.closingSoonPromptDisabled}
											edited={formEdited}
											setIsLoading={setIsLoading}
											setPrompt={(prompt) => {
												setNewSettings([{ newValue: prompt, fieldName: "closingSoonPrompt" }]);
											}}
										/>
									)}
									<Grid item xs={12} className="pb-0">
										<strong>
											<span className="w-100 m-0">
												Closed Prompt
												<Checkbox
													onChange={(event) => {
														onContentModified(true);
														setNewSettings([{ newValue: !event.target.checked, fieldName: "closedPromptDisabled" }]);
													}}
													checked={!settings?.closedPromptDisabled}
												></Checkbox>
											</span>
										</strong>
									</Grid>
									{lookups.languages && lookups.namespaces && (
										<PromptsSelector
											languages={lookups.languages}
											namespaces={lookups.namespaces}
											prompt={settings?.closedPrompt}
											disabled={settings?.closedPromptDisabled}
											edited={formEdited}
											setIsLoading={setIsLoading}
											setPrompt={(prompt) => {
												setNewSettings([{ newValue: prompt, fieldName: "closedPrompt" }]);
											}}
										/>
									)}
								</Grid>
							</div>
						</div>
					)}
				</div>
			</>
		</CardContent>
	);
};

export default ClosedMessagesTab;
